// Admin Invite Email Template Component
// This shows what the invitation email would look like

import React from 'react';

interface AdminInviteEmailProps {
  inviteEmail: string;
  inviteToken: string;
  invitedBy: string;
}

const AdminInviteEmail: React.FC<AdminInviteEmailProps> = ({ 
  inviteEmail, 
  inviteToken, 
  invitedBy 
}) => {
  const signupUrl = `${window.location.origin}/admin/signup?invite=${inviteToken}&email=${encodeURIComponent(inviteEmail)}`;

  return (
    <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg p-8">
      <div className="text-center mb-6">
        <div className="mx-auto h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
          <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Invitation</h1>
        <p className="text-gray-600">You've been invited to become an admin</p>
      </div>

      <div className="space-y-4 text-gray-700">
        <p>Hello,</p>
        
        <p>
          <strong>{invitedBy}</strong> has invited you to become an administrator for Scubamail. 
          As an admin, you'll have access to the admin dashboard where you can manage users, 
          templates, and system settings.
        </p>

        <div className="bg-gray-50 rounded-lg p-4 my-6">
          <h3 className="font-semibold text-gray-900 mb-2">What you can do as an admin:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Manage user accounts and permissions</li>
            <li>Create and manage email templates</li>
            <li>View system analytics and reports</li>
            <li>Configure system settings</li>
            <li>Invite other administrators</li>
          </ul>
        </div>

        <p>To accept this invitation and create your admin account, click the button below:</p>

        <div className="text-center my-6">
          <a
            href={signupUrl}
            className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors duration-200"
          >
            Accept Invitation & Create Account
          </a>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">Important:</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
            <li>This invitation expires in 7 days</li>
            <li>You must use the email address: <strong>{inviteEmail}</strong></li>
            <li>If you don't have access to this email, contact the administrator</li>
          </ul>
        </div>

        <p className="text-sm text-gray-500 mt-6">
          If the button doesn't work, you can copy and paste this link into your browser:
        </p>
        <p className="text-sm text-blue-600 break-all bg-gray-100 p-2 rounded">
          {signupUrl}
        </p>

        <p className="text-sm text-gray-500 mt-4">
          If you didn't expect this invitation, you can safely ignore this email.
        </p>

        <hr className="my-6" />
        
        <p className="text-xs text-gray-500">
          This invitation was sent by {invitedBy} on {new Date().toLocaleDateString()}.
        </p>
      </div>
    </div>
  );
};

export default AdminInviteEmail;
